import React from 'react';
import SiteLayout from '../../layouts/SiteLayout';
import Box from '../../components/Common/Box';

const DocsScreen: React.FC = () => {
    return (
        <SiteLayout>
            <div className="content-wrapper">
                <h1 className="page-title">📚 Guide Utilisateur Kanea</h1>

                <Box>
                    <div className="box-vertical-padding box-horizontal-padding">
                        <div className="docs-content">
                            {/* Table des matières */}
                            <section id="toc">
                                <h2>Table des matières</h2>
                                <ol>
                                    <li><a href="#introduction">Introduction</a></li>
                                    <li><a href="#getting-started">Démarrage (Inscription, Connexion)</a></li>
                                    <li><a href="#inside-market">Inside Market</a>
                                        <ul style={{ marginTop: 5 }}>
                                            <li><a href="#swing-trader">📈 Swing Trader</a></li>
                                        </ul>
                                    </li>
                                    <li><a href="#virtual-account">Virtual Account</a>
                                        <ul style={{ marginTop: 5 }}>
                                            <li><a href="#rebalancing">🎯 Rééquilibrage Intelligent</a></li>
                                        </ul>
                                    </li>
                                    <li><a href="#settings">Settings</a></li>
                                    <li><a href="#administration">Administration</a></li>
                                    <li><a href="#tenant-admin">Tenant Admin</a></li>
                                    <li><a href="#fiscalite">Fiscalité des Dividendes (2025)</a></li>
                                </ol>
                            </section>

                            <hr className="divider" />

                            {/* Introduction */}
                            <section id="introduction">
                                <h2>1. Introduction</h2>
                                <p>
                                    <strong>Kanea</strong> est une plateforme complète pour l'analyse et le suivi du marché boursier régional (BRVM).
                                    Elle offre des outils d'analyse technique, un portefeuille virtuel, et des fonctionnalités d'administration multi-tenant.
                                </p>
                            </section>

                            <hr className="divider" />

                            {/* Démarrage */}
                            <section id="getting-started">
                                <h2>2. Démarrage</h2>

                                <h3>2.1 Inscription</h3>
                                <p>Tous les champs sont obligatoires :</p>
                                <ul>
                                    <li>Email (format valide requis)</li>
                                    <li>Mot de passe (min. 8 caractères)</li>
                                    <li>Confirmation du mot de passe</li>
                                    <li>Nom et Prénom</li>
                                    <li>Date de naissance (Jour, Mois, Année)</li>
                                    <li>Téléphone (Indicatif pays + numéro)</li>
                                    <li>Acceptation des conditions d'utilisation</li>
                                </ul>

                                <h3>2.2 Vérification d'Email</h3>
                                <p>
                                    Un email de vérification est envoyé après inscription. Cliquez sur le lien pour activer votre compte (lien valide 24h).
                                    Option "Renvoyer l'email" disponible si nécessaire.
                                </p>

                                <h3>2.3 Connexion et Mot de passe oublié</h3>
                                <p>
                                    Connexion avec email/mot de passe. La fonction "Forgot password" envoie un lien de réinitialisation par email.
                                </p>
                            </section>

                            <hr className="divider" />

                            {/* Inside Market */}
                            <section id="inside-market">
                                <h2>3. 📊 Inside Market</h2>
                                <p><em>Permission requise : view_market_data</em></p>

                                <div className="feature-grid">
                                    <div className="feature-item">
                                        <h4>🏠 Dashboard</h4>
                                        <ul className="feature-list">
                                            <li><strong>Indices BRVM :</strong> BRVM Composite, BRVM 10 avec valeur et variation</li>
                                            <li><strong>Sentiment du marché :</strong> Nombre de hausses, baisses, inchangées</li>
                                            <li><strong>Top Gainers/Losers :</strong> 5 meilleures et pires performances du jour</li>
                                            <li><strong>Statistiques :</strong> Capitalisation boursière, valeur échangée</li>
                                            <li><strong>Accès rapides :</strong> Calendrier, Actualités, Capital</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>📈 Capital</h4>
                                        <ul className="feature-list">
                                            <li>Liste de toutes les sociétés cotées</li>
                                            <li>Cours actuel, variation journalière (%)</li>
                                            <li>Capitalisation boursière</li>
                                            <li>Clic sur une ligne → détails de l'action</li>
                                            <li>Lignes alternées pour lisibilité</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>📅 Calendrier</h4>
                                        <ul className="feature-list">
                                            <li>Assemblées Générales à venir</li>
                                            <li>Date, heure et lieu</li>
                                            <li>Type : Ordinaire / Extraordinaire</li>
                                            <li>Regroupement par mois</li>
                                            <li>Filtres par type d'AG</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>📰 Actualités</h4>
                                        <ul className="feature-list">
                                            <li>Communiqués des émetteurs BRVM</li>
                                            <li>Statistiques par type (résultats, dividendes...)</li>
                                            <li>Barre de recherche</li>
                                            <li>Cartes expansibles avec contenu complet</li>
                                            <li>Filtres par type d'actualité</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>📊 Data & Analytics (Avancé)</h4>
                                        <p><strong>3 onglets :</strong></p>
                                        <p><em>📊 Charts :</em></p>
                                        <ul className="feature-list">
                                            <li>Sélection d'une société</li>
                                            <li>4 types de graphiques : Line, Candlestick, Heikin-Ashi, Area</li>
                                            <li>Historique des cotations (200 jours)</li>
                                        </ul>
                                        <p><em>📈 Indicateurs :</em></p>
                                        <ul className="feature-list">
                                            <li>Analyse technique automatisée (interprétation IA)</li>
                                            <li>Bollinger Bands (20, 2)</li>
                                            <li>RSI (14) - surachat/survente</li>
                                            <li>MACD (12, 26, 9) - histogramme + lignes</li>
                                            <li>SMA 20 - Moyenne mobile simple</li>
                                        </ul>
                                        <p><em>💰 Dividendes :</em></p>
                                        <ul className="feature-list">
                                            <li>Historique des dividendes versés</li>
                                            <li>Graphique en barres des montants</li>
                                            <li>Tableau détaillé (date, montant)</li>
                                            <li>Analyse du rendement dividende</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item" style={{ gridColumn: '1 / -1' }}>
                                        <h4>🔍 Analyse Globale (Avancé)</h4>
                                        <p><em>Scan complet du marché avec indicateurs avancés</em></p>

                                        <p><strong>📊 KPIs Globaux :</strong></p>
                                        <ul className="feature-list">
                                            <li>Mean Perf (YTD) : Performance moyenne annuelle</li>
                                            <li>Median Perf (YTD) : Médiane des performances</li>
                                            <li>% Actions en hausse : Ratio d'actions positives</li>
                                            <li>Volatilité Moyenne 30j : Risque moyen du marché</li>
                                        </ul>

                                        <p><strong>📈 Tableaux d'analyse :</strong></p>
                                        <ul className="feature-list">
                                            <li>🏆 Top Performances (YTD) : Meilleures progressions annuelles</li>
                                            <li>📉 Flops (YTD) : Pires performances annuelles</li>
                                            <li>⚡ Plus Volatiles (30j) : Actions les plus risquées</li>
                                            <li>💰 Top Rendements : Meilleurs dividendes</li>
                                            <li>📅 Régularité (3+ ans) : Payeurs de dividendes constants</li>
                                        </ul>

                                        <p><strong>🏢 Analyse Sectorielle :</strong></p>
                                        <ul className="feature-list">
                                            <li>Performance par secteur</li>
                                            <li>Rendement moyen par secteur</li>
                                            <li>Volatilité par secteur</li>
                                            <li>Nombre de titres par secteur</li>
                                        </ul>

                                        <p><strong>📆 Saisonnalité :</strong></p>
                                        <ul className="feature-list">
                                            <li>Variation moyenne par mois</li>
                                            <li>Taux de réussite (% positif) par mois</li>
                                            <li>Graphique en barres horizontales</li>
                                        </ul>

                                        <p><strong>🔎 Scan Complet :</strong></p>
                                        <ul className="feature-list">
                                            <li>Tableau de toutes les sociétés (jusqu'à 500)</li>
                                            <li>Colonnes : Symbole, Perf YTD, Vol30, RSI, Liquidité, Dividende Régulier, Dividend Yield</li>
                                            <li>Export CSV disponible</li>
                                            <li>Mode sombre (Beta)</li>
                                        </ul>

                                        <p><strong>⚠️ Indicateur Vol 30j (Risque) :</strong></p>
                                        <p><em>Volatilité historique annualisée sur 30 jours.</em></p>
                                        <ul style={{ fontSize: 13, listStyle: 'none', paddingLeft: 0 }}>
                                            <li style={{ marginBottom: 4 }}><span style={{ background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: 4, fontWeight: 'bold' }}>&lt; 20%</span> : Volatilité faible (Stable)</li>
                                            <li style={{ marginBottom: 4 }}><span style={{ background: '#fef3c7', color: '#d97706', padding: '2px 8px', borderRadius: 4, fontWeight: 'bold' }}>20% - 40%</span> : Volatilité moyenne</li>
                                            <li><span style={{ background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: 4, fontWeight: 'bold' }}>&gt; 40%</span> : Volatilité élevée (Risqué)</li>
                                        </ul>

                                        <p><strong>📊 Système de notation de régularité du dividende :</strong></p>
                                        <p><em>On attribue 1 point par année avec dividende versé sur les 5 dernières années.</em></p>
                                        <table style={{ width: '100%', marginTop: 10, marginBottom: 15, fontSize: 13 }}>
                                            <thead>
                                                <tr style={{ background: '#f1f5f9' }}>
                                                    <th style={{ padding: 8, textAlign: 'left' }}>Score</th>
                                                    <th style={{ padding: 8, textAlign: 'left' }}>Label</th>
                                                    <th style={{ padding: 8, textAlign: 'left' }}>Définition</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td style={{ padding: 8 }}>5/5</td>
                                                    <td style={{ padding: 8 }}><span style={{ background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: 4, fontWeight: 'bold' }}>Très régulier</span></td>
                                                    <td style={{ padding: 8 }}>Dividende maintenu chaque année sur les 5 dernières années</td>
                                                </tr>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td style={{ padding: 8 }}>3 à 4/5</td>
                                                    <td style={{ padding: 8 }}><span style={{ background: '#fef3c7', color: '#d97706', padding: '2px 8px', borderRadius: 4, fontWeight: 'bold' }}>Régulier</span></td>
                                                    <td style={{ padding: 8 }}>Dividende versé de façon majoritairement stable</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: 8 }}>1 à 2/5</td>
                                                    <td style={{ padding: 8 }}><span style={{ background: '#fed7aa', color: '#ea580c', padding: '2px 8px', borderRadius: 4, fontWeight: 'bold' }}>Peu régulier</span></td>
                                                    <td style={{ padding: 8 }}>Dividende irrégulier, interruptions fréquentes</td>
                                                </tr>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td style={{ padding: 8 }}>0/5</td>
                                                    <td style={{ padding: 8 }}><span style={{ background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: 4, fontWeight: 'bold' }}>Non régulier</span></td>
                                                    <td style={{ padding: 8 }}>Absence de dividende sur les 5 dernières années</td>
                                                </tr>
                                            </tbody>
                                        </table>

                                        <p><strong>🎯 Score Technique (0-100) :</strong></p>
                                        <p><em>Score composite basé sur 3 indicateurs techniques. Base = 50 points.</em></p>
                                        <table style={{ width: '100%', marginTop: 10, marginBottom: 15, fontSize: 13 }}>
                                            <thead>
                                                <tr style={{ background: '#f1f5f9' }}>
                                                    <th style={{ padding: 8, textAlign: 'left' }}>Indicateur</th>
                                                    <th style={{ padding: 8, textAlign: 'left' }}>Condition</th>
                                                    <th style={{ padding: 8, textAlign: 'center' }}>Points</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td style={{ padding: 8 }} rowSpan={4}><strong>RSI (14)</strong></td>
                                                    <td style={{ padding: 8 }}>RSI &lt; 30 (survendu)</td>
                                                    <td style={{ padding: 8, textAlign: 'center', color: '#16a34a' }}>+15</td>
                                                </tr>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td style={{ padding: 8 }}>RSI 30-40</td>
                                                    <td style={{ padding: 8, textAlign: 'center', color: '#16a34a' }}>+5</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: 8 }}>RSI 60-70</td>
                                                    <td style={{ padding: 8, textAlign: 'center', color: '#dc2626' }}>-5</td>
                                                </tr>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td style={{ padding: 8 }}>RSI &gt; 70 (suracheté)</td>
                                                    <td style={{ padding: 8, textAlign: 'center', color: '#dc2626' }}>-15</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: 8 }} rowSpan={4}><strong>Tendance (MA)</strong></td>
                                                    <td style={{ padding: 8 }}>Prix &gt; MA20 &gt; MA50 (haussier fort)</td>
                                                    <td style={{ padding: 8, textAlign: 'center', color: '#16a34a' }}>+30</td>
                                                </tr>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td style={{ padding: 8 }}>Prix &gt; MA20 (haussier modéré)</td>
                                                    <td style={{ padding: 8, textAlign: 'center', color: '#16a34a' }}>+15</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: 8 }}>Prix &lt; MA20 (baissier modéré)</td>
                                                    <td style={{ padding: 8, textAlign: 'center', color: '#dc2626' }}>-15</td>
                                                </tr>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td style={{ padding: 8 }}>Prix &lt; MA20 &lt; MA50 (baissier fort)</td>
                                                    <td style={{ padding: 8, textAlign: 'center', color: '#dc2626' }}>-30</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: 8 }} rowSpan={4}><strong>Momentum 6M</strong></td>
                                                    <td style={{ padding: 8 }}>Perf 6M &gt; +15%</td>
                                                    <td style={{ padding: 8, textAlign: 'center', color: '#16a34a' }}>+20</td>
                                                </tr>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td style={{ padding: 8 }}>Perf 6M +5% à +15%</td>
                                                    <td style={{ padding: 8, textAlign: 'center', color: '#16a34a' }}>+10</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: 8 }}>Perf 6M -15% à -5%</td>
                                                    <td style={{ padding: 8, textAlign: 'center', color: '#dc2626' }}>-10</td>
                                                </tr>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td style={{ padding: 8 }}>Perf 6M &lt; -15%</td>
                                                    <td style={{ padding: 8, textAlign: 'center', color: '#dc2626' }}>-20</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <p><strong>Interprétation :</strong> <span style={{ background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: 4, fontWeight: 'bold' }}>70-100 Positif</span> <span style={{ background: '#dbeafe', color: '#2563eb', padding: '2px 8px', borderRadius: 4, fontWeight: 'bold', marginLeft: 8 }}>40-69 Neutre</span> <span style={{ background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: 4, fontWeight: 'bold', marginLeft: 8 }}>0-39 Négatif</span></p>

                                        <p><strong>💧 Double Score de Liquidité (0-100) :</strong></p>
                                        <p><em>Deux scores adaptés à différents profils d'investisseurs sur la BRVM.</em></p>

                                        <p><strong>📈 Score Long Terme (LT) - Investisseurs (6 mois) :</strong></p>
                                        <table style={{ width: '100%', marginTop: 10, marginBottom: 15, fontSize: 13 }}>
                                            <thead>
                                                <tr style={{ background: '#f1f5f9' }}>
                                                    <th style={{ padding: 8, textAlign: 'left' }}>Composant</th>
                                                    <th style={{ padding: 8, textAlign: 'center' }}>Poids</th>
                                                    <th style={{ padding: 8, textAlign: 'left' }}>Description</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td style={{ padding: 8 }}>Rotation</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>50%</strong></td>
                                                    <td style={{ padding: 8 }}>Moyenne du turnover ratio sur 130j (seuil: 0.1%)</td>
                                                </tr>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td style={{ padding: 8 }}>Régularité</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>30%</strong></td>
                                                    <td style={{ padding: 8 }}>% de jours avec volume {">"} 0 sur 130j</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: 8 }}>Profondeur</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>20%</strong></td>
                                                    <td style={{ padding: 8 }}>Valeur moyenne échangée (seuil: 5M FCFA)</td>
                                                </tr>
                                            </tbody>
                                        </table>

                                        <p><strong>⚡ Score Court Terme (CT) - Traders (20 jours) :</strong></p>
                                        <table style={{ width: '100%', marginTop: 10, marginBottom: 15, fontSize: 13 }}>
                                            <thead>
                                                <tr style={{ background: '#f1f5f9' }}>
                                                    <th style={{ padding: 8, textAlign: 'left' }}>Composant</th>
                                                    <th style={{ padding: 8, textAlign: 'center' }}>Poids</th>
                                                    <th style={{ padding: 8, textAlign: 'left' }}>Description</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td style={{ padding: 8 }}>Activité</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>50%</strong></td>
                                                    <td style={{ padding: 8 }}>% de jours avec volume {">"} 0 sur 20j</td>
                                                </tr>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td style={{ padding: 8 }}>Carnet</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>30%</strong></td>
                                                    <td style={{ padding: 8 }}>Présence d'ordres achat ET vente</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: 8 }}>Bonus Volume</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>20%</strong></td>
                                                    <td style={{ padding: 8 }}>Volume du jour {">"} 0</td>
                                                </tr>
                                            </tbody>
                                        </table>

                                        <p><strong>⛔ Kill Switch BRVM (Court Terme) :</strong></p>
                                        <p style={{ fontSize: 13, background: '#fee2e2', padding: 12, borderRadius: 8, color: '#dc2626' }}>
                                            Si sur les 3 derniers jours, une séance a eu une variation ≥ 7% ET un volume = 0,
                                            le score CT est plafonné à 20 et le titre est marqué <strong>BLOQUÉ</strong>.
                                            Cela protège les traders des titres "reservés" sans contrepartie.
                                        </p>

                                        <p><strong>Grades de liquidité :</strong></p>
                                        <ul style={{ fontSize: 13, listStyle: 'none', paddingLeft: 0 }}>
                                            <li style={{ marginBottom: 4 }}><span style={{ fontSize: 16 }}>🟢</span> <span style={{ background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: 4, fontWeight: 'bold' }}>EXCELLENTE (70-100)</span> : Liquidité optimale</li>
                                            <li style={{ marginBottom: 4 }}><span style={{ fontSize: 16 }}>🟡</span> <span style={{ background: '#fef3c7', color: '#d97706', padding: '2px 8px', borderRadius: 4, fontWeight: 'bold' }}>FRAGILE (50-69)</span> : Liquidité acceptable</li>
                                            <li style={{ marginBottom: 4 }}><span style={{ fontSize: 16 }}>🟠</span> <span style={{ background: '#fed7aa', color: '#ea580c', padding: '2px 8px', borderRadius: 4, fontWeight: 'bold' }}>FAIBLE (30-49)</span> : Liquidité limitée</li>
                                            <li style={{ marginBottom: 4 }}><span style={{ fontSize: 16 }}>🔴</span> <span style={{ background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: 4, fontWeight: 'bold' }}>ILLIQUIDE/BLOQUÉ (0-29)</span> : Difficile à négocier</li>
                                        </ul>

                                        <p><strong>Utilisation par fonctionnalité :</strong></p>
                                        <table style={{ width: '100%', marginTop: 10, marginBottom: 15, fontSize: 13 }}>
                                            <thead>
                                                <tr style={{ background: '#f1f5f9' }}>
                                                    <th style={{ padding: 8, textAlign: 'left' }}>Fonctionnalité</th>
                                                    <th style={{ padding: 8, textAlign: 'center' }}>Score utilisé</th>
                                                    <th style={{ padding: 8, textAlign: 'left' }}>Logique</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td style={{ padding: 8 }}>Swing Trader</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>CT (≥60)</strong></td>
                                                    <td style={{ padding: 8 }}>Traders ont besoin de liquidité immédiate + Kill Switch</td>
                                                </tr>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td style={{ padding: 8 }}>Trend Surfer</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>CT (≥70)</strong></td>
                                                    <td style={{ padding: 8 }}>Stratégie momentum, liquidité forte requise</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: 8 }}>Rééquilibrage</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>min(LT, CT)</strong></td>
                                                    <td style={{ padding: 8 }}>Approche prudente : les deux conditions doivent être remplies</td>
                                                </tr>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td style={{ padding: 8 }}>Analyse Globale</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>LT + CT affichés</strong></td>
                                                    <td style={{ padding: 8 }}>Les deux scores sont visibles dans le Scanner Pro</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </section>

                            <hr className="divider" />

                            {/* Swing Trader */}
                            <section id="swing-trader">
                                <h2>📈 Swing Trader</h2>
                                <p><em>Permission requise : view_advanced_data</em></p>
                                <p>Outil d'aide à la décision pour les stratégies de trading court/moyen terme sur la BRVM.</p>

                                <div className="feature-grid">
                                    <div className="feature-item" style={{ gridColumn: '1 / -1' }}>
                                        <h4>📊 Onglets disponibles</h4>
                                        <ul className="feature-list">
                                            <li><strong>Valeurs à explorer</strong> : Actions répondant aux critères de sélection (score ≥ 70)</li>
                                            <li><strong>Alertes baissières</strong> : Actions en portefeuille avec indicateurs techniques défavorables</li>
                                            <li><strong>Univers Éligible</strong> : Tableau complet de toutes les actions analysées</li>
                                            <li><strong>Info</strong> : Explication de la méthodologie</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>🔥 Stratégie Swing (Rebond)</h4>
                                        <p><em>Approche contrariante : identifier les actions survendues</em></p>
                                        <p><strong>Critères (Score 0-100) :</strong></p>
                                        <ul className="feature-list">
                                            <li><strong>RSI (25 pts)</strong> : RSI &lt; 30 = +25, RSI 30-40 = +15, RSI &gt; 70 = -20</li>
                                            <li><strong>Momentum (25 pts)</strong> : Variation 5j &gt; +10% = +25</li>
                                            <li><strong>Volume (20 pts)</strong> : Ratio volume &gt; 2.5x = +20</li>
                                            <li><strong>Breakout (20 pts)</strong> : Proche du support 20j = +20</li>
                                            <li><strong>Liquidité (10 pts)</strong> : Score ≥ 60 requis</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>🏄 Stratégie Trend Surfer</h4>
                                        <p><em>Approche momentum : suivre les tendances établies</em></p>
                                        <p><strong>Critères (Score 0-100) :</strong></p>
                                        <ul className="feature-list">
                                            <li><strong>Trend Filter (40 pts)</strong> : Cours &gt; SMA50 &gt; SMA100</li>
                                            <li><strong>Momentum 6M (25 pts)</strong> : Progression 6 mois &gt; 0%</li>
                                            <li><strong>RSI Sweet Spot (20 pts)</strong> : RSI entre 45 et 65</li>
                                            <li><strong>Liquidité (15 pts)</strong> : Score ≥ 70 requis</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item" style={{ gridColumn: '1 / -1' }}>
                                        <h4>💰 Position Sizing (Dimensionnement)</h4>
                                        <table style={{ width: '100%', marginTop: 10, marginBottom: 15, fontSize: 13 }}>
                                            <thead>
                                                <tr style={{ background: '#f1f5f9' }}>
                                                    <th style={{ padding: 8, textAlign: 'left' }}>Paramètre</th>
                                                    <th style={{ padding: 8, textAlign: 'center' }}>Valeur</th>
                                                    <th style={{ padding: 8, textAlign: 'left' }}>Description</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td style={{ padding: 8 }}>Max par position</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>5%</strong></td>
                                                    <td style={{ padding: 8 }}>Du portefeuille total</td>
                                                </tr>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td style={{ padding: 8 }}>Risque par trade</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>2%</strong></td>
                                                    <td style={{ padding: 8 }}>Perte max acceptable</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: 8 }}>Stop-loss</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>7.5%</strong></td>
                                                    <td style={{ padding: 8 }}>Limite BRVM journalière</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <p style={{ fontSize: 12, color: '#64748b' }}>
                                            ⚠️ Contraintes BRVM : Règlement J+2, variation max ±7.5%/jour, liquidité variable.
                                        </p>
                                    </div>
                                </div>
                            </section>

                            <hr className="divider" />

                            {/* Virtual Account */}
                            <section id="virtual-account">
                                <h2>4. 💼 Virtual Account</h2>
                                <p><em>Permission requise : manage_portfolio</em></p>

                                <div className="feature-grid">
                                    <div className="feature-item">
                                        <h4>👛 My Wallet</h4>
                                        <ul className="feature-list">
                                            <li>Solde cash disponible</li>
                                            <li>Valeur totale du portefeuille</li>
                                            <li>Performance globale (+/-)</li>
                                            <li>Répartition des actifs</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>🔄 Transactions</h4>
                                        <ul className="feature-list">
                                            <li>Historique achats/ventes</li>
                                            <li>Date, quantité, prix unitaire</li>
                                            <li>Montant total de chaque transaction</li>
                                            <li>Filtres par type (BUY/SELL)</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>🏦 Deposit / Withdraw</h4>
                                        <ul className="feature-list">
                                            <li>Dépôt de liquidités virtuelles</li>
                                            <li>Retrait de fonds</li>
                                            <li>Formulaire avec validation</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>📈 My Assets</h4>
                                        <ul className="feature-list">
                                            <li>Actions détenues</li>
                                            <li>Quantité par titre</li>
                                            <li>PRU (Prix de Revient Unitaire)</li>
                                            <li>Valeur actuelle et plus/moins-value</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>📊 Analyse Portefeuille (Avancé)</h4>
                                        <ul className="feature-list">
                                            <li>Allocation sectorielle (graphique)</li>
                                            <li>Performance vs benchmark</li>
                                            <li>Calcul de volatilité</li>
                                            <li>Idées de diversification</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>⭐ My Favorites</h4>
                                        <ul className="feature-list">
                                            <li>Liste de surveillance personnalisée</li>
                                            <li>Ajout/suppression depuis page action</li>
                                            <li>Accès rapide aux cours</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item" style={{ gridColumn: '1 / -1' }}>
                                        <h4>🎯 Rééquilibrage Intelligent (Avancé)</h4>
                                        <p><em>Permission requise : view_advanced_data</em></p>

                                        <p><strong>📊 Profil d'investisseur cible :</strong></p>
                                        <ul className="feature-list">
                                            <li><strong>Investisseur Value Long Terme</strong> (horizon 3-10 ans)</li>
                                            <li>Tolérance au risque : Modérée</li>
                                            <li>Objectif : Accumulation de patrimoine + revenus dividendes</li>
                                            <li>Implication : Semi-passive (révision trimestrielle)</li>
                                            <li>Connaissance requise : Intermédiaire à avancée</li>
                                        </ul>

                                        <p><strong>⚠️ Profils NON adaptés :</strong></p>
                                        <ul className="feature-list">
                                            <li>Day traders (pas d'indicateurs court terme)</li>
                                            <li>Investisseurs momentum (on identifie les décotés, pas les tendances)</li>
                                            <li>Stratégies ultra-concentrées (limitation à 15% max par titre)</li>
                                        </ul>

                                        <p><strong>🎯 Score Value Tilt (0-100) :</strong></p>
                                        <p><em>Score composite basé sur 5 composants pondérés.</em></p>
                                        <table style={{ width: '100%', marginTop: 10, marginBottom: 15, fontSize: 13 }}>
                                            <thead>
                                                <tr style={{ background: '#f1f5f9' }}>
                                                    <th style={{ padding: 8, textAlign: 'left' }}>Composant</th>
                                                    <th style={{ padding: 8, textAlign: 'center' }}>Pondération</th>
                                                    <th style={{ padding: 8, textAlign: 'left' }}>Source de données</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td style={{ padding: 8 }}>Sous-valorisation</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>40%</strong></td>
                                                    <td style={{ padding: 8 }}>potentiel_pct (11 modèles de valorisation)</td>
                                                </tr>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td style={{ padding: 8 }}>Qualité financière</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>25%</strong></td>
                                                    <td style={{ padding: 8 }}>ROE, Dette/EBITDA*, Croissance CA</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: 8 }}>Stabilité dividendes</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>15%</strong></td>
                                                    <td style={{ padding: 8 }}>Historique 5 ans, CAGR, Yield actuel</td>
                                                </tr>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td style={{ padding: 8 }}>Liquidité</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>10%</strong></td>
                                                    <td style={{ padding: 8 }}>Volume moyen, jours de cotation</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: 8 }}>Impact diversification</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>10%</strong></td>
                                                    <td style={{ padding: 8 }}>Calcul dynamique selon portefeuille</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <p style={{ fontSize: 12, color: '#64748b' }}>* Les banques et assurances n'utilisent pas l'EBITDA ; le ROE est pondéré plus fortement pour ces secteurs.</p>

                                        <p><strong>📏 Règles d'allocation :</strong></p>
                                        <table style={{ width: '100%', marginTop: 10, marginBottom: 15, fontSize: 13 }}>
                                            <thead>
                                                <tr style={{ background: '#f1f5f9' }}>
                                                    <th style={{ padding: 8, textAlign: 'left' }}>Règle</th>
                                                    <th style={{ padding: 8, textAlign: 'center' }}>Valeur</th>
                                                    <th style={{ padding: 8, textAlign: 'left' }}>Description</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td style={{ padding: 8 }}>Max par action</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>15%</strong></td>
                                                    <td style={{ padding: 8 }}>Éviter la sur-concentration sur un titre</td>
                                                </tr>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td style={{ padding: 8 }}>Max par secteur</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>40%</strong></td>
                                                    <td style={{ padding: 8 }}>Diversification sectorielle obligatoire</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: 8 }}>Nombre de titres cible</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>8-15</strong></td>
                                                    <td style={{ padding: 8 }}>Portefeuille diversifié mais gérable</td>
                                                </tr>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td style={{ padding: 8 }}>Score min. de sélection</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>60/100</strong></td>
                                                    <td style={{ padding: 8 }}>Seuil de qualité pour nouvelles positions</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: 8 }}>Min. par nouvelle position</td>
                                                    <td style={{ padding: 8, textAlign: 'center' }}><strong>2%</strong></td>
                                                    <td style={{ padding: 8 }}>Allocation minimale significative</td>
                                                </tr>
                                            </tbody>
                                        </table>

                                        <p><strong>💡 Types de suggestions :</strong></p>
                                        <ul style={{ fontSize: 13, listStyle: 'none', paddingLeft: 0 }}>
                                            <li style={{ marginBottom: 4 }}><span style={{ background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: 4, fontWeight: 'bold' }}>À EXPLORER</span> : Nouvelle position sur une action avec score ≥ 60</li>
                                            <li style={{ marginBottom: 4 }}><span style={{ background: '#dbeafe', color: '#2563eb', padding: '2px 8px', borderRadius: 4, fontWeight: 'bold' }}>TENDANCE_HAUSSIÈRE</span> : Augmenter une position existante sous-pondérée</li>
                                            <li style={{ marginBottom: 4 }}><span style={{ background: '#fef3c7', color: '#d97706', padding: '2px 8px', borderRadius: 4, fontWeight: 'bold' }}>ÉQUILIBRÉ</span> : Position équilibrée, aucune action requise</li>
                                            <li style={{ marginBottom: 4 }}><span style={{ background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: 4, fontWeight: 'bold' }}>SUREXPOSITION</span> : Sur-pondérée (&gt;15%) ou score faible (&lt;30)</li>
                                        </ul>

                                        <p><strong>⚠️ Avertissement :</strong></p>
                                        <p style={{ fontSize: 13, background: '#fef3c7', padding: 12, borderRadius: 8, color: '#92400e' }}>
                                            Ces suggestions sont générées automatiquement à partir de règles d'allocation et de valorisation reconnues
                                            et ne constituent pas un conseil financier personnalisé. Consultez un conseiller financier agréé avant
                                            toute décision d'investissement.
                                        </p>
                                    </div>
                                </div>
                            </section>

                            <hr className="divider" />

                            {/* Settings */}
                            <section id="settings">
                                <h2>5. ⚙️ Settings</h2>

                                <div className="feature-grid">
                                    <div className="feature-item">
                                        <h4>👤 My Profile</h4>
                                        <ul className="feature-list">
                                            <li>Informations personnelles</li>
                                            <li>Changement de mot de passe</li>
                                            <li>Photo de profil</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>🔔 Alert Settings</h4>
                                        <ul className="feature-list">
                                            <li>Création d'alertes de prix</li>
                                            <li>Types : Prix au-dessus/en-dessous, variation %</li>
                                            <li>Notification email + in-app</li>
                                            <li>Gestion des alertes actives</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>📧 Messages</h4>
                                        <ul className="feature-list">
                                            <li>Alertes déclenchées</li>
                                            <li>Notifications système</li>
                                            <li>Messages administrateur</li>
                                            <li>Marquer comme lu/non lu</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>📬 Contacts</h4>
                                        <ul className="feature-list">
                                            <li>Formulaire de contact</li>
                                            <li>Envoi de message au support</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>📚 Docs</h4>
                                        <ul className="feature-list">
                                            <li>Cette page (Guide utilisateur)</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            <hr className="divider" />

                            {/* Administration */}
                            <section id="administration">
                                <h2>6. 🛡️ Administration</h2>
                                <p><em>Permission requise : admin_platform (Super Admin uniquement)</em></p>

                                <div className="feature-grid">
                                    <div className="feature-item">
                                        <h4>📊 Dashboard Global</h4>
                                        <ul className="feature-list">
                                            <li>KPIs : Utilisateurs, Tenants, Logs</li>
                                            <li>Dernières connexions</li>
                                            <li>Activité récente</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>🏢 Tenants</h4>
                                        <ul className="feature-list">
                                            <li>Liste des organisations</li>
                                            <li>Création/modification de tenant</li>
                                            <li>Plan d'abonnement</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>🔐 Rôles & Permissions</h4>
                                        <ul className="feature-list">
                                            <li>Définition des rôles</li>
                                            <li>Attribution des permissions</li>
                                            <li>Matrice rôle-permission</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>👥 Utilisateurs (Global)</h4>
                                        <ul className="feature-list">
                                            <li>Liste de tous les utilisateurs</li>
                                            <li>Création, modification, désactivation</li>
                                            <li>Attribution tenant/rôle</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>📋 Logs & Audit</h4>
                                        <ul className="feature-list">
                                            <li>Historique des actions</li>
                                            <li>Connexions, modifications</li>
                                            <li>Filtres par utilisateur/action</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>💳 Paiements / Plans</h4>
                                        <ul className="feature-list">
                                            <li>Gestion des abonnements</li>
                                            <li>Plans tarifaires</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>🔧 Maintenance</h4>
                                        <ul className="feature-list">
                                            <li>État du système</li>
                                            <li>Jobs planifiés</li>
                                            <li>Cache</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>📡 Monitoring Azure</h4>
                                        <ul className="feature-list">
                                            <li>Métriques infrastructure</li>
                                            <li>Alertes Azure</li>
                                            <li>Santé des services</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>✉️ Users Messages</h4>
                                        <ul className="feature-list">
                                            <li>Messages des utilisateurs</li>
                                            <li>Panneau de détail</li>
                                            <li>Suppression</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>🏛️ Sociétés</h4>
                                        <ul className="feature-list">
                                            <li>Liste des sociétés cotées</li>
                                            <li>Symbole, nom, secteur</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            <hr className="divider" />

                            {/* Tenant Admin */}
                            <section id="tenant-admin">
                                <h2>7. 🏢 Tenant Admin</h2>
                                <p><em>Permission requise : manage_tenant_settings (Admin d'organisation)</em></p>

                                <div className="feature-grid">
                                    <div className="feature-item">
                                        <h4>📊 Tenant Dashboard</h4>
                                        <ul className="feature-list">
                                            <li>Statistiques de l'organisation</li>
                                            <li>Utilisateurs actifs</li>
                                            <li>Activité récente</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>👥 Tenant Users</h4>
                                        <ul className="feature-list">
                                            <li>Membres de l'organisation</li>
                                            <li>Ajout/suppression</li>
                                            <li>Attribution de rôles locaux</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>🔐 Rôles Locaux</h4>
                                        <ul className="feature-list">
                                            <li>Rôles spécifiques au tenant</li>
                                            <li>Permissions locales</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>⚙️ Paramètres</h4>
                                        <ul className="feature-list">
                                            <li>Nom de l'organisation</li>
                                            <li>Email de notification</li>
                                            <li>Adresse</li>
                                            <li>Plan d'abonnement</li>
                                        </ul>
                                    </div>

                                    <div className="feature-item">
                                        <h4>🔔 Notifications</h4>
                                        <ul className="feature-list">
                                            <li>Préférences de notification</li>
                                            <li>Alertes organisation</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            <hr className="divider" />

                            {/* Fiscalité */}
                            <section id="fiscalite">
                                <h2>8. 💰 Synthèse de la Fiscalité des Dividendes dans l'UEMOA (2025)</h2>
                                <p>
                                    La fiscalité des valeurs mobilières dans l'Union est encadrée par la Directive n° 02/2010/CM/UEMOA, qui fixe des fourchettes d'imposition pour harmoniser le marché régional.
                                    Cependant, chaque État membre conserve la souveraineté de fixer son taux exact via ses Lois de Finances, ce qui crée des disparités effectives constatées lors des paiements de dividendes en 2025.
                                </p>

                                <h3>1. Les Taux Intermédiaires (12 % et 12,5 %)</h3>
                                <p>Bien que proches, ces taux relèvent de régimes distincts :</p>
                                <div className="feature-grid">
                                    <div className="feature-item">
                                        <h4>🇨🇮 Côte d'Ivoire : 12 % (Taux Hybride)</h4>
                                        <ul className="feature-list">
                                            <li><strong>Application :</strong> Ce taux s'applique spécifiquement aux personnes physiques (particuliers). Les personnes morales (entreprises) restent généralement imposées au taux standard de 10 %.</li>
                                            <li><strong>Texte applicable :</strong> Code Général des Impôts ivoirien modifié par l'Annexe Fiscale 2024/2025.</li>
                                            <li><strong>Preuve :</strong> Les avis de paiement (ex: Nestlé CI, Orange CI) dans le Bulletin Officiel confirment ce prélèvement différencié.</li>
                                        </ul>
                                    </div>
                                    <div className="feature-item">
                                        <h4>🇧🇫 Burkina Faso : 12,5 % (Taux Unique)</h4>
                                        <ul className="feature-list">
                                            <li><strong>Application :</strong> Taux unique de retenue à la source applicable à tous les bénéficiaires, sans distinction.</li>
                                            <li><strong>Texte applicable :</strong> Code Général des Impôts burkinabè.</li>
                                            <li><strong>Preuve :</strong> Le calcul sur les dividendes d'Onatel BF montre une retenue stricte de 12,5 % (Brut 216,61 FCFA → Net 189,53 FCFA).</li>
                                        </ul>
                                    </div>
                                </div>

                                <h3>2. Le Taux Standard UEMOA (10 %)</h3>
                                <p>Conforme au plancher fixé par la Directive pour les dividendes ordinaires.</p>
                                <div className="feature-grid">
                                    <div className="feature-item">
                                        <h4>🇸🇳 Sénégal : 10 %</h4>
                                        <p>Appliqué par défaut (ex: Sonatel, BOA Sénégal) selon le Code Général des Impôts sénégalais.</p>
                                    </div>
                                    <div className="feature-item">
                                        <h4>🇬🇼 Guinée-Bissau : 10 % (Théorique)</h4>
                                        <p>Faute de société cotée, le taux légal par défaut s'applique.</p>
                                    </div>
                                </div>

                                <h3>3. Les Taux Incitatifs (5 % et 7 %)</h3>
                                <p>Ces pays utilisent la fourchette basse autorisée par la Directive (2% à 7%) pour encourager l'investissement boursier.</p>
                                <div className="feature-grid">
                                    <div className="feature-item">
                                        <h4>🇧🇯 Bénin : 5 %</h4>
                                        <p>Le taux le plus attractif de la région pour les sociétés cotées (ex: BOA Bénin), conformément au Code Général des Impôts béninois.</p>
                                    </div>
                                    <div className="feature-item">
                                        <h4>🇲🇱 Mali & 🇳🇪 Niger : 7 %</h4>
                                        <p>Taux réduit appliqué uniformément (ex: BOA Mali, BOA Niger).</p>
                                    </div>
                                    <div className="feature-item">
                                        <h4>🇹🇬 Togo : 7 % (Cas général)</h4>
                                        <p>Taux standard pour les sociétés cotées (ex: Oragroup).</p>
                                        <p><strong>Exception notable :</strong> Le titre ETIT (Ecobank) bénéficie souvent d'un taux effectif de 0 % en raison de son statut juridique particulier (accord de siège/zone franche).</p>
                                    </div>
                                </div>
                            </section>

                            <hr className="divider" />

                            {/* Support */}
                            <section id="support">
                                <h2>📞 Support</h2>
                                <p>
                                    Pour toute question : <strong>Settings → Contacts</strong> ou <strong>support@kaneabourse.com</strong>
                                </p>
                            </section>
                        </div>
                    </div>
                </Box>
            </div>
            <style>{`
                .docs-content {
                    line-height: 1.6;
                    color: #333;
                }
                .docs-content h2 {
                    color: #2563eb;
                    border-bottom: 2px solid #e5e7eb;
                    padding-bottom: 10px;
                    margin-top: 30px;
                }
                .docs-content h3 {
                    color: #4b5563;
                    margin-top: 20px;
                }
                .docs-content h4 {
                    color: #1e293b;
                    margin: 0 0 10px 0;
                }
                .docs-content ul, .docs-content ol {
                    margin-left: 20px;
                    margin-bottom: 15px;
                }
                .docs-content li {
                    margin-bottom: 5px;
                }
                .docs-content a {
                    color: #2563eb;
                    text-decoration: none;
                }
                .docs-content a:hover {
                    text-decoration: underline;
                }
                .divider {
                    border: 0;
                    border-top: 1px solid #e5e7eb;
                    margin: 40px 0;
                }
                .feature-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                    gap: 16px;
                    margin-top: 20px;
                }
                .feature-item {
                    background: #f8fafc;
                    border-radius: 8px;
                    padding: 16px;
                    border: 1px solid #e2e8f0;
                }
                .feature-item p {
                    margin: 8px 0;
                    font-size: 14px;
                    color: #64748b;
                }
                .feature-list {
                    margin: 8px 0 0 16px !important;
                    padding: 0;
                }
                .feature-list li {
                    font-size: 13px;
                    color: #475569;
                    margin-bottom: 4px;
                }
            `}</style>
        </SiteLayout>
    );
};

export default DocsScreen;
